/**
 * CAT-002 — Catalog Filters Modal
 * Full-screen modal for detailed catalog filtering
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Slider } from '@/components/ui/slider';
import { ChevronLeft, ChevronDown, Search, X, Check } from 'lucide-react';
import { BrushHighlight } from '@/components/BrushHighlight';
import { CityAutocomplete, type City } from './CityAutocomplete';
import { BreedAutocomplete } from './BreedAutocomplete';
import type { CatalogFiltersState } from '@/hooks/useCatalogFilters';

// Assets
import dogIllustration from '@/assets/illustration-filter-dog.png';
import catIllustration from '@/assets/illustration-filter-cat.png';

// ============ Types ============
interface CatalogFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialFilters: CatalogFiltersState;
  onApply: (draft: Partial<CatalogFiltersState>) => void;
}

interface DraftFilters {
  speciesKey: string | null;
  breedIds: string[];
  breedNames: Record<string, string>;
  sex: string[];
  ageBuckets: string[];
  geoPlaceId: string | null;
  geoPlaceName: string | null;
  // Character slider value: null = не активен, 0-100 = выбрано
  characterValue: number | null;
  getsAlongWith: string[];
  // Appearance tab
  size: string[];
  color: string | null;
  // Health tab
  healthSpecialNeeds: boolean | null;
  healthCategory: string | null; // 'minor' | 'needs_attention' | 'special' | 'unique'
}

// ============ Constants ============
const SEX_LABELS: Record<string, string> = {
  female: 'Девочка',
  male: 'Мальчик',
};

const AGE_LABELS: Record<string, string> = {
  puppy: 'Щенок 0-1',
  kitten: 'Котёнок 0-1',
  young: 'Юный 1-3',
  adult: 'Взрослый 3-7',
  senior: 'Пожилой 7+',
};

const ANY_AGE_OPTION = { value: 'any', label: 'Любой' };

const DOG_AGE_OPTIONS = [
  ANY_AGE_OPTION,
  { value: 'puppy', label: 'Щенок (до 1 года)' },
  { value: 'young', label: 'Юный (1-3 года)' },
  { value: 'adult', label: 'Взрослый (3-7 лет)' },
  { value: 'senior', label: 'Пожилой (7+ лет)' },
];

const CAT_AGE_OPTIONS = [
  ANY_AGE_OPTION,
  { value: 'kitten', label: 'Котёнок (до 1 года)' },
  { value: 'young', label: 'Юный (1-3 года)' },
  { value: 'adult', label: 'Взрослый (3-7 лет)' },
  { value: 'senior', label: 'Пожилой (7+ лет)' },
];

const GETS_ALONG_OPTIONS = [
  { value: 'dogs', label: 'С собаками' },
  { value: 'cats', label: 'С кошками' },
  { value: 'kids', label: 'С детьми' },
  { value: 'everyone', label: 'Со всеми' },
];

const SIZE_OPTIONS = [
  { id: 'xs', label: 'XS Мини' },
  { id: 's', label: 'S Маленькие' },
  { id: 'm', label: 'M Средние' },
  { id: 'l', label: 'L Большие' },
];

const COLOR_OPTIONS = [
  { id: 'white', label: 'Белый' },
  { id: 'red', label: 'Рыжий' },
  { id: 'black', label: 'Чёрный' },
  { id: 'tortie', label: 'Черепаховый' },
  { id: 'gray', label: 'Серый' },
  { id: 'other', label: 'Другой' },
];

// Character filter now uses slider, these constants are for display only
const CHARACTER_LABELS = {
  introvert: 'Интроверт',
  extrovert: 'Экстраверт',
};

// Health subcategories (UI-only for now)
const HEALTH_CATEGORIES = [
  { id: 'minor', label: 'Небольшие отклонения' },
  { id: 'needs_attention', label: 'Требует внимания' },
  { id: 'special', label: 'Особые потребности' },
  { id: 'unique', label: 'Уникальный случай' },
];

type TabKey = 'characteristics' | 'appearance' | 'health';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'characteristics', label: 'Характеристики' },
  { key: 'appearance', label: 'Внешний вид' },
  { key: 'health', label: 'Здоровье' },
];

// ============ Helper: map initial filters to draft ============
function mapFiltersToDraft(filters: CatalogFiltersState): DraftFilters {
  // Обратный маппинг: characterTypes → characterValue
  // Зоны: 0-34 = интроверт, 35-85 = нейтральная (все), 86-100 = экстраверт
  let characterValue: number | null = null;
  const charTypes = filters.characterTypes || [];
  if (charTypes.includes('introvert') && !charTypes.includes('extrovert')) {
    characterValue = 17; // Интроверт - середина левой зоны (0-35)
  } else if (charTypes.includes('extrovert') && !charTypes.includes('introvert')) {
    characterValue = 92; // Экстраверт - середина правой зоны (85-100)
  }
  // Если оба или пустой массив — null (не активен)

  return {
    speciesKey: filters.speciesKey,
    breedIds: filters.breedIds,
    breedNames: filters.breedNames || {},
    sex: filters.sex,
    ageBuckets: filters.ageBuckets,
    geoPlaceId: filters.geoPlaceId,
    geoPlaceName: filters.geoPlaceName,
    characterValue,
    getsAlongWith: [],
    // Map extended filters (v2) from initialFilters
    size: filters.sizes || [],
    color: filters.colors?.[0] || null,
    healthSpecialNeeds: filters.healthSpecialNeeds ?? null,
    healthCategory: null,
  };
}

// ============ Sub-components ============

// Filter Card wrapper
const FilterCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="bg-white rounded-[20px] p-4 mb-3">
    <h3 className="text-lg font-bold text-[#3F2516] mb-3">{title}</h3>
    {children}
  </div>
);

// Circular checkbox
const FilterCheckbox = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) => (
  <button type="button" onClick={onChange} className="flex items-center gap-2">
    <span
      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
        checked ? 'bg-[#3F2516] border-[#3F2516]' : 'bg-[#E0E0E0] border-[#E0E0E0]'
      }`}
    >
      <Check className={`w-4 h-4 ${checked ? 'text-white' : 'text-white/60'}`} />
    </span>
    <span className="text-base text-[#3F2516]">{label}</span>
  </button>
);

// Active filter chip
const ActiveChip = ({ label, onRemove }: { label: string; onRemove: () => void }) => (
  <span className="inline-flex items-center gap-1.5 h-[36px] px-3 bg-[#FDE7EF] text-[#3F2516] text-sm font-medium rounded-[12px]">
    {label}
    <button type="button" onClick={onRemove} className="w-7 h-7 flex items-center justify-center -mr-1">
      <X className="w-4 h-4" />
    </button>
  </span>
);

// ============ Main Component ============
export function CatalogFiltersModal({
  isOpen,
  onClose,
  initialFilters,
  onApply,
}: CatalogFiltersModalProps) {
  // Step logic: 1 = species selection, 2 = detailed filters
  const [currentStep, setCurrentStep] = useState<1 | 2>(() =>
    initialFilters.speciesKey ? 2 : 1
  );

  // Draft filters (local state)
  const [draft, setDraft] = useState<DraftFilters>(() => mapFiltersToDraft(initialFilters));

  // Snapshot on open for "Reset" functionality
  const [snapshotOnOpen, setSnapshotOnOpen] = useState<DraftFilters | null>(null);

  // Active tab
  const [activeTab, setActiveTab] = useState<TabKey>('characteristics');

  // City picker state
  const [isCityPickerOpen, setIsCityPickerOpen] = useState(false);

  // Age dropdown state
  const [isAgeDropdownOpen, setIsAgeDropdownOpen] = useState(false);

  // Color dropdown state
  const [isColorDropdownOpen, setIsColorDropdownOpen] = useState(false);

  // Initialize on open
  useEffect(() => {
    if (isOpen) {
      const snapshot = mapFiltersToDraft(initialFilters);
      setDraft(snapshot);
      setSnapshotOnOpen(snapshot);
      setCurrentStep(initialFilters.speciesKey ? 2 : 1);
      setActiveTab('characteristics');
      setIsCityPickerOpen(false);
      setIsAgeDropdownOpen(false);
      setIsColorDropdownOpen(false);
    }
  }, [isOpen, initialFilters]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  // Close without saving (revert to initial)
  const handleClose = useCallback(() => {
    setDraft(mapFiltersToDraft(initialFilters));
    onClose();
  }, [initialFilters, onClose]);

  // Reset all fields to clean state, keep speciesKey
  const handleReset = useCallback(() => {
    setDraft({
      speciesKey: draft.speciesKey, // Keep current species selection
      breedIds: [],
      breedNames: {},
      sex: [],
      ageBuckets: [],
      geoPlaceId: null,
      geoPlaceName: null,
      characterValue: null, // Сбросить в неактивное состояние
      getsAlongWith: [],
      size: [],
      color: null,
      healthSpecialNeeds: null,
      healthCategory: null,
    });
  }, [draft.speciesKey]);

  // Apply filters and close
  const handleApply = useCallback(() => {
    // Маппинг characterValue → characterTypes
    // Зоны: 0-34 = интроверт, 35-85 = нейтральная (все), 86-100 = экстраверт
    let characterTypes: string[] = [];
    if (draft.characterValue !== null) {
      if (draft.characterValue < 35) {
        characterTypes = ['introvert'];
      } else if (draft.characterValue > 85) {
        characterTypes = ['extrovert'];
      }
      // 35-85: characterTypes остаётся пустым = нет фильтра
    }
    
    onApply({
      speciesKey: draft.speciesKey,
      breedIds: draft.breedIds,
      breedNames: draft.breedNames,
      sex: draft.sex,
      ageBuckets: draft.ageBuckets,
      geoPlaceId: draft.geoPlaceId,
      geoPlaceName: draft.geoPlaceName,
      // Extended filters (v2)
      sizes: draft.size,
      colors: draft.color ? [draft.color] : [],
      healthSpecialNeeds: draft.healthSpecialNeeds,
      characterTypes,
    });
  }, [draft, onApply]);

  // Toggle sex in draft
  const toggleSex = (s: string) => {
    setDraft((d) => ({
      ...d,
      sex: d.sex.includes(s) ? d.sex.filter((x) => x !== s) : [...d.sex, s],
    }));
  };

  // Toggle size
  const toggleSize = (sizeId: string) => {
    setDraft((d) => ({
      ...d,
      size: d.size.includes(sizeId)
        ? d.size.filter((x) => x !== sizeId)
        : [...d.size, sizeId],
    }));
  };

  // Toggle gets along with
  const toggleGetsAlongWith = (value: string) => {
    setDraft((d) => ({
      ...d,
      getsAlongWith: d.getsAlongWith.includes(value)
        ? d.getsAlongWith.filter((x) => x !== value)
        : [...d.getsAlongWith, value],
    }));
  };

  // Check if we have active chips to show
  const hasActiveChips =
    (draft.sex?.length ?? 0) > 0 ||
    (draft.ageBuckets?.length ?? 0) > 0 ||
    Object.keys(draft.breedNames ?? {}).length > 0 ||
    draft.geoPlaceName ||
    (draft.size?.length ?? 0) > 0 ||
    draft.color ||
    draft.healthSpecialNeeds !== null ||
    draft.characterValue !== null;

  // Age options based on species
  const ageOptions = draft.speciesKey === 'cat' ? CAT_AGE_OPTIONS : DOG_AGE_OPTIONS;

  if (!isOpen) return null;

  // ============ Step 1: Species Selection ============
  if (currentStep === 1) {
    return (
      <div className="fixed inset-0 z-50 flex justify-center">
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

        {/* Content */}
        <div className="relative w-full max-w-[480px] h-[100dvh] overflow-hidden flex flex-col bg-white">
          {/* Background illustration */}
          <div className="absolute inset-0 z-0">
            <img
              src={draft.speciesKey === 'cat' ? catIllustration : dogIllustration}
              alt=""
              className="w-full h-full object-cover object-top transition-opacity duration-300"
            />
          </div>

          {/* Top card */}
          <div
            className="relative z-10 bg-white rounded-b-[32px] pb-6 px-4 shadow-sm"
            style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
          >
            {/* Back button */}
            <div className="flex items-center pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="w-10 h-10 flex items-center justify-center -ml-2"
              >
                <ChevronLeft className="w-6 h-6 text-[#3F2516]" />
              </button>
            </div>

            {/* Title */}
            <div className="mt-2">
              <h1
                className="text-[40px] font-black text-[#3F2516] leading-[0.8]"
                style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
              >
                кого
                <br />
                <BrushHighlight color="#FFC0DC" rotateDeg={-2}>
                  ищем?
                </BrushHighlight>
              </h1>
            </div>
          </div>

          <div className="flex-1" />

          {/* Bottom controls */}
          <div
            className="relative z-10 px-4 bg-gradient-to-t from-white via-white to-transparent pt-8"
            style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
          >
            {/* Species pills */}
            <div className="flex gap-3 mb-3">
              <button
                type="button"
                onClick={() => setDraft((d) => ({ ...d, speciesKey: 'dog', breedIds: [], breedNames: {} }))}
                className={`flex-1 h-[48px] rounded-[20px] font-medium text-base transition-colors ${
                  draft.speciesKey === 'dog'
                    ? 'bg-[#3F2516] text-white'
                    : 'bg-[#FFC0DC] text-[#3F2516]'
                }`}
              >
                Собаку
              </button>
              <button
                type="button"
                onClick={() => setDraft((d) => ({ ...d, speciesKey: 'cat', breedIds: [], breedNames: {} }))}
                className={`flex-1 h-[48px] rounded-[20px] font-medium text-base transition-colors ${
                  draft.speciesKey === 'cat'
                    ? 'bg-[#3F2516] text-white'
                    : 'bg-[#FFC0DC] text-[#3F2516]'
                }`}
              >
                Кошку
              </button>
            </div>

            {/* Continue CTA */}
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              disabled={!draft.speciesKey}
              className="w-full h-[56px] rounded-[20px] bg-[#3F2516] text-white font-medium text-base disabled:bg-[#F7F7F7] disabled:text-[#A1A1A1] transition-colors"
            >
              Продолжить
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============ Step 2: Detailed Filters ============
  return (
    <div className="fixed inset-0 z-50 flex justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Content */}
      <div className="relative w-full max-w-[480px] h-[100dvh] overflow-hidden flex flex-col bg-[#F4F4F4]">
        {/* Fixed header */}
        <div
          className="bg-[#F4F4F4] px-4 z-20"
          style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}
        >
          <div className="flex items-center justify-between h-14">
            <button
              type="button"
              onClick={initialFilters.speciesKey ? handleClose : () => setCurrentStep(1)}
              className="w-10 h-10 flex items-center justify-center -ml-2"
            >
              <ChevronLeft className="w-6 h-6 text-[#3F2516]" />
            </button>
            <span
              className="text-lg font-bold text-[#3F2516] absolute left-1/2 -translate-x-1/2"
              style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
            >
              Фильтры
            </span>
            <button
              type="button"
              onClick={handleReset}
              className="text-sm font-medium text-[#9E9E9E]"
            >
              Сбросить
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 pb-[100px]">
          {/* City input */}
          <div className="mb-4">
            {isCityPickerOpen ? (
              <CityAutocomplete
                value={draft.geoPlaceName || ''}
                cityId={draft.geoPlaceId}
                onSelect={(city: City | null) => {
                  setDraft((d) => ({
                    ...d,
                    geoPlaceId: city?.id ?? null,
                    geoPlaceName: city?.name ?? null,
                  }));
                  setIsCityPickerOpen(false);
                }}
                placeholder="Выберите город"
                showIcon={false}
              />
            ) : (
              <button
                type="button"
                onClick={() => setIsCityPickerOpen(true)}
                className="w-full h-[52px] bg-[#F4F4F4] rounded-[12px] px-4 flex items-center justify-between border border-[#EAEAEA]"
              >
                <span className={`text-base ${draft.geoPlaceName ? 'text-[#9E9E9E]' : 'text-[#9E9E9E]'}`}>
                  {draft.geoPlaceName || 'Выберите город'}
                </span>
                <Search className="w-6 h-6 text-[#3F2516]" />
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-4 border-b border-[#EAEAEA] mb-4 sticky top-0 bg-[#F4F4F4] pt-1 z-10 -mx-4 px-4">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`pb-2 text-sm flex items-center gap-1 transition-colors ${
                  activeTab === tab.key
                    ? 'text-[#3F2516] font-bold border-b-2 border-[#3F2516]'
                    : 'text-[#3F2516]/50 font-medium'
                }`}
              >
                {tab.label}
                <Check className="w-4 h-4" />
              </button>
            ))}
          </div>

          {/* Active filter chips */}
          {hasActiveChips && (
            <div className="flex flex-wrap gap-2 mb-4">
              {draft.sex.map((s) => (
                <ActiveChip
                  key={s}
                  label={SEX_LABELS[s]}
                  onRemove={() => setDraft((d) => ({ ...d, sex: d.sex.filter((x) => x !== s) }))}
                />
              ))}
              {draft.ageBuckets.map((b) => (
                <ActiveChip
                  key={b}
                  label={AGE_LABELS[b] || b}
                  onRemove={() => setDraft((d) => ({ ...d, ageBuckets: [] }))}
                />
              ))}
              {Object.entries(draft.breedNames).map(([breedId, breedName]) => (
                <ActiveChip
                  key={breedId}
                  label={breedName}
                  onRemove={() => setDraft((d) => {
                    const { [breedId]: _, ...restNames } = d.breedNames;
                    return {
                      ...d,
                      breedIds: d.breedIds.filter((id) => id !== breedId),
                      breedNames: restNames,
                    };
                  })}
                />
              ))}
              {draft.geoPlaceName && (
                <ActiveChip
                  label={draft.geoPlaceName}
                  onRemove={() => setDraft((d) => ({ ...d, geoPlaceId: null, geoPlaceName: null }))}
                />
              )}
              {draft.size.map((s) => (
                <ActiveChip
                  key={`size-${s}`}
                  label={SIZE_OPTIONS.find((o) => o.id === s)?.label || s}
                  onRemove={() => setDraft((d) => ({ ...d, size: d.size.filter((x) => x !== s) }))}
                />
              ))}
              {draft.color && (
                <ActiveChip
                  label={COLOR_OPTIONS.find((o) => o.id === draft.color)?.label || draft.color}
                  onRemove={() => setDraft((d) => ({ ...d, color: null }))}
                />
              )}
              {draft.healthSpecialNeeds !== null && (
                <ActiveChip
                  label={draft.healthSpecialNeeds ? 'Есть особенности' : 'Нет особенностей'}
                  onRemove={() => setDraft((d) => ({ ...d, healthSpecialNeeds: null }))}
                />
              )}
              {draft.characterValue !== null && (
                <ActiveChip
                  label={draft.characterValue < 50 ? CHARACTER_LABELS.introvert : CHARACTER_LABELS.extrovert}
                  onRemove={() => setDraft((d) => ({ ...d, characterValue: null }))}
                />
              )}
            </div>
          )}

          {/* Tab content: Characteristics */}
          {activeTab === 'characteristics' && (
            <>
              {/* Breed card */}
              <FilterCard title="Порода">
                <BreedAutocomplete
                  speciesKey={(draft.speciesKey as 'dog' | 'cat') || 'dog'}
                  value={Object.values(draft.breedNames)[0] || ''}
                  breedId={draft.breedIds[0] || null}
                  onSelect={(breed) => {
                    setDraft((d) => ({
                      ...d,
                      breedIds: breed ? [...d.breedIds.filter(id => id !== breed.id), breed.id] : d.breedIds,
                      breedNames: breed ? { ...d.breedNames, [breed.id]: breed.name } : d.breedNames,
                    }));
                  }}
                  placeholder="Выберите породу"
                />
              </FilterCard>

              {/* Sex card */}
              <FilterCard title="Пол">
                <div className="flex gap-8">
                  <FilterCheckbox
                    label="Девочка"
                    checked={draft.sex.includes('female')}
                    onChange={() => toggleSex('female')}
                  />
                  <FilterCheckbox
                    label="Мальчик"
                    checked={draft.sex.includes('male')}
                    onChange={() => toggleSex('male')}
                  />
                </div>
              </FilterCard>

              {/* Age card */}
              <FilterCard title="Возраст">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsAgeDropdownOpen(!isAgeDropdownOpen)}
                    className="w-full h-[52px] bg-[#F4F4F4] rounded-[12px] px-4 flex items-center justify-between"
                  >
                    <span className={draft.ageBuckets.length > 0 ? 'text-[#3F2516]' : 'text-[#9E9E9E]'}>
                      {draft.ageBuckets.length > 0
                        ? ageOptions.find((o) => o.value === draft.ageBuckets[0])?.label || 'Укажите возраст'
                        : 'Укажите возраст'}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-[#3F2516] transition-transform ${isAgeDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {/* Age dropdown */}
                  {isAgeDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-[12px] shadow-lg border border-[#EAEAEA] z-50 overflow-hidden">
                      {ageOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            if (opt.value === 'any') {
                              setDraft((d) => ({ ...d, ageBuckets: [] }));
                            } else {
                              setDraft((d) => ({ ...d, ageBuckets: [opt.value] }));
                            }
                            setIsAgeDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-3 text-left text-sm flex items-center justify-between hover:bg-[#F4F4F4] transition-colors ${
                            opt.value === 'any' 
                              ? draft.ageBuckets.length === 0 ? 'bg-[#FDE7EF]' : ''
                              : draft.ageBuckets.includes(opt.value) ? 'bg-[#FDE7EF]' : ''
                          }`}
                        >
                          <span className="text-[#3F2516]">{opt.label}</span>
                          {(opt.value === 'any' ? draft.ageBuckets.length === 0 : draft.ageBuckets.includes(opt.value)) && (
                            <Check className="w-4 h-4 text-[#3F2516]" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </FilterCard>

              {/* Personality slider - Introvert / Extrovert */}
              <FilterCard title="Характер">
                <div className="px-2">
                  {/* Слайдер с визуальным выделением при активации */}
                  <div className={`rounded-[16px] p-4 transition-colors ${
                    draft.characterValue !== null 
                      ? 'bg-[#FDE7EF]'  // Активен — розовый фон
                      : 'bg-[#F4F4F4]' // Не активен — серый фон
                  }`}>
                    <Slider
                      value={[draft.characterValue ?? 50]}
                      onValueChange={([value]) => {
                        setDraft(d => ({ ...d, characterValue: value }));
                      }}
                      min={0}
                      max={100}
                      step={1}
                      variant="pink"
                      className="w-full"
                    />
                    <div className="flex justify-between mt-3 text-sm">
                      <span className={
                        draft.characterValue !== null && draft.characterValue < 35 
                          ? 'text-[#3F2516] font-medium' 
                          : 'text-[#9E9E9E]'
                      }>
                        Интроверт
                      </span>
                      <span className={
                        draft.characterValue !== null && draft.characterValue > 85 
                          ? 'text-[#3F2516] font-medium' 
                          : 'text-[#9E9E9E]'
                      }>
                        Экстраверт
                      </span>
                    </div>
                  </div>
                  
                  {/* Кнопка сброса — показывать только когда активен */}
                  {draft.characterValue !== null && (
                    <button
                      type="button"
                      onClick={() => setDraft(d => ({ ...d, characterValue: null }))}
                      className="mt-2 text-sm text-[#9E9E9E] underline"
                    >
                      Сбросить
                    </button>
                  )}
                </div>
              </FilterCard>

              {/* Gets along with card (UI-only) */}
              <FilterCard title="Ладит с">
                <div className="grid grid-cols-2 gap-3">
                  {GETS_ALONG_OPTIONS.map((opt) => (
                    <FilterCheckbox
                      key={opt.value}
                      label={opt.label}
                      checked={draft.getsAlongWith.includes(opt.value)}
                      onChange={() => toggleGetsAlongWith(opt.value)}
                    />
                  ))}
                </div>
              </FilterCard>
            </>
          )}

          {/* Tab content: Appearance */}
          {activeTab === 'appearance' && (
            <>
              {/* Size card */}
              <FilterCard title="Размер">
                <div className="grid grid-cols-2 gap-3">
                  {SIZE_OPTIONS.map((opt) => (
                    <FilterCheckbox
                      key={opt.id}
                      label={opt.label}
                      checked={draft.size.includes(opt.id)}
                      onChange={() => toggleSize(opt.id)}
                    />
                  ))}
                </div>
              </FilterCard>

              {/* Color card */}
              <FilterCard title="Окрас">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsColorDropdownOpen(!isColorDropdownOpen)}
                    className="w-full h-[52px] bg-[#F4F4F4] rounded-[12px] px-4 flex items-center justify-between"
                  >
                    <span className={draft.color ? 'text-[#3F2516]' : 'text-[#9E9E9E]'}>
                      {draft.color
                        ? COLOR_OPTIONS.find((o) => o.id === draft.color)?.label || 'Выберите окрас'
                        : 'Выберите окрас'}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-[#3F2516] transition-transform ${isColorDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {/* Color dropdown */}
                  {isColorDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-[12px] shadow-lg border border-[#EAEAEA] z-50 overflow-hidden">
                      {COLOR_OPTIONS.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => {
                            setDraft((d) => ({ ...d, color: opt.id }));
                            setIsColorDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-3 text-left text-sm flex items-center justify-between hover:bg-[#F4F4F4] transition-colors ${
                            draft.color === opt.id ? 'bg-[#FDE7EF]' : ''
                          }`}
                        >
                          <span className="text-[#3F2516]">{opt.label}</span>
                          {draft.color === opt.id && (
                            <Check className="w-4 h-4 text-[#3F2516]" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </FilterCard>
            </>
          )}

          {/* Tab content: Health */}
          {activeTab === 'health' && (
            <div className="space-y-3">
              {/* No special needs */}
              <button
                type="button"
                onClick={() => setDraft((d) => ({ ...d, healthSpecialNeeds: false, healthCategory: null }))}
                className={`w-full flex items-center gap-4 p-4 rounded-[20px] bg-white transition-colors ${
                  draft.healthSpecialNeeds === false ? 'ring-2 ring-[#3F2516]' : ''
                }`}
              >
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                    draft.healthSpecialNeeds === false ? 'bg-[#3F2516]' : 'bg-[#E0E0E0]'
                  }`}
                >
                  <Check className={`w-4 h-4 ${draft.healthSpecialNeeds === false ? 'text-white' : 'text-white/60'}`} />
                </span>
                <span className="text-[#3F2516] text-base font-medium">Нет особенностей</span>
              </button>

              {/* Has special needs + subcategories */}
              <div className={`rounded-[20px] bg-white overflow-hidden transition-colors ${
                draft.healthSpecialNeeds === true ? 'ring-2 ring-[#3F2516]' : ''
              }`}>
                <button
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, healthSpecialNeeds: true }))}
                  className="w-full flex items-center gap-4 p-4"
                >
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                      draft.healthSpecialNeeds === true ? 'bg-[#3F2516]' : 'bg-[#E0E0E0]'
                    }`}
                  >
                    <Check className={`w-4 h-4 ${draft.healthSpecialNeeds === true ? 'text-white' : 'text-white/60'}`} />
                  </span>
                  <span className="text-[#3F2516] text-base font-medium">Есть особенности</span>
                </button>
                
                {/* Subcategories - visible when "Has special needs" is selected */}
                {draft.healthSpecialNeeds === true && (
                  <div className="border-t border-[#EAEAEA] px-4 pb-4 pt-2 space-y-2">
                    {HEALTH_CATEGORIES.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setDraft(d => ({ 
                          ...d, 
                          healthCategory: d.healthCategory === cat.id ? null : cat.id 
                        }))}
                        className={`w-full text-left p-3 rounded-[12px] text-sm transition-colors ${
                          draft.healthCategory === cat.id 
                            ? 'bg-[#FDE7EF] text-[#3F2516] font-medium' 
                            : 'bg-[#F4F4F4] text-[#3F2516]/70'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Fixed footer button */}
        <div
          className="fixed bottom-0 left-0 right-0 flex justify-center z-20 bg-gradient-to-t from-[#F4F4F4] via-[#F4F4F4] to-transparent pt-4"
          style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
        >
          <div className="w-full max-w-[480px] px-4">
            <button
              type="button"
              onClick={handleApply}
              className="w-full h-[56px] rounded-[20px] bg-[#3F2516] text-white font-bold text-base shadow-lg"
              style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
            >
              Показать объявления
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CatalogFiltersModal;
